import React, { useState, useEffect } from 'react';
import { Plus, Search, FileText, Calendar, Edit2, Trash2, User, Home } from 'lucide-react';
import api from '../lib/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Modal from '../components/Modal';
import { Input, Select } from '../components/Form';

interface Property {
  id: string;
  code: string;
  title: string;
}

interface Person {
  id: string;
  name: string;
  document: string;
}

interface Lease {
  id: string;
  code: string;
  status: string;
  type: string;
  startDate: string;
  endDate?: string;
  rentAmount: number;
  depositAmount?: number;
  property?: Property;
  tenant?: Person;
  owner?: Person;
  guarantor?: Person;
  isActive: boolean;
}

export default function LeasesPage() {
  const [leases, setLeases] = useState<Lease[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLease, setSelectedLease] = useState<Lease | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    type: 'RENT',
    status: 'ACTIVE',
    propertyId: '',
    tenantId: '',
    ownerId: '',
    guarantorId: '',
    startDate: '',
    endDate: '',
    rentAmount: 0,
    depositAmount: 0,
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [leasesRes, propertiesRes, peopleRes] = await Promise.all([
        api.get('/leases'),
        api.get('/properties'),
        api.get('/people'),
      ]);

      setLeases(leasesRes.data.data || []);
      setProperties(propertiesRes.data.data || []);
      setPeople(peopleRes.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (lease?: Lease) => {
    if (lease) {
      setSelectedLease(lease);
      setFormData({
        code: lease.code || '',
        type: lease.type || 'RENT',
        status: lease.status || 'ACTIVE',
        propertyId: lease.property?.id || '',
        tenantId: lease.tenant?.id || '',
        ownerId: lease.owner?.id || '',
        guarantorId: lease.guarantor?.id || '',
        startDate: lease.startDate ? lease.startDate.split('T')[0] : '',
        endDate: lease.endDate ? lease.endDate.split('T')[0] : '',
        rentAmount: lease.rentAmount || 0,
        depositAmount: lease.depositAmount || 0,
        isActive: lease.isActive !== undefined ? lease.isActive : true,
      });
    } else {
      setSelectedLease(null);
      setFormData({
        code: '',
        type: 'RENT',
        status: 'ACTIVE',
        propertyId: '',
        tenantId: '',
        ownerId: '',
        guarantorId: '',
        startDate: '',
        endDate: '',
        rentAmount: 0,
        depositAmount: 0,
        isActive: true,
      });
    }
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedLease(null);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Código é obrigatório';
    }

    if (!formData.propertyId) {
      newErrors.propertyId = 'Imóvel é obrigatório';
    }

    if (!formData.tenantId) {
      newErrors.tenantId = 'Inquilino é obrigatório';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Data de início é obrigatória';
    }

    if (formData.rentAmount <= 0) {
      newErrors.rentAmount = 'Valor do aluguel deve ser maior que zero';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        ...formData,
        rentAmount: Number(formData.rentAmount),
        depositAmount: Number(formData.depositAmount) || undefined,
        endDate: formData.endDate || undefined,
        guarantorId: formData.guarantorId || undefined,
        ownerId: formData.ownerId || undefined,
      };

      if (selectedLease) {
        await api.put(`/leases/${selectedLease.id}`, payload);
      } else {
        await api.post('/leases', payload);
      }

      await loadData();
      handleCloseModal();
    } catch (error: any) {
      console.error('Erro ao salvar contrato:', error);
      if (error.response?.data?.message) {
        setErrors({ submit: error.response.data.message });
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedLease) return;

    try {
      await api.delete(`/leases/${selectedLease.id}`);
      await loadData();
      setShowDeleteModal(false);
      setSelectedLease(null);
    } catch (error) {
      console.error('Erro ao excluir contrato:', error);
    }
  };

  const filteredLeases = leases.filter((lease) => {
    const matchesSearch =
      lease.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lease.property?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lease.tenant?.name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'ALL' || lease.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const statusLabels: Record<string, string> = {
    DRAFT: 'Rascunho',
    ACTIVE: 'Ativo',
    TERMINATED: 'Encerrado',
    SUSPENDED: 'Suspenso',
  };

  const statusColors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-800',
    ACTIVE: 'bg-green-100 text-green-800',
    TERMINATED: 'bg-red-100 text-red-800',
    SUSPENDED: 'bg-yellow-100 text-yellow-800',
  };

  const typeLabels: Record<string, string> = {
    RENT: 'Locação',
    SALE: 'Venda',
    LEASE: 'Arrendamento',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Contratos</h1>
        <button onClick={() => handleOpenModal()} className="btn-primary flex items-center space-x-2">
          <Plus className="w-5 h-5" />
          <span>Novo Contrato</span>
        </button>
      </div>

      <div className="card space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por código, imóvel ou inquilino..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>

        <div className="flex space-x-2">
          {['ALL', 'ACTIVE', 'DRAFT', 'TERMINATED', 'SUSPENDED'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === status
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status === 'ALL' ? 'Todos' : statusLabels[status]}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredLeases.map((lease) => (
          <div key={lease.id} className="card hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="bg-primary-100 p-3 rounded-lg">
                  <FileText className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">{lease.code}</h3>
                  <p className="text-sm text-gray-600">{typeLabels[lease.type]}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[lease.status]}`}>
                {statusLabels[lease.status]}
              </span>
            </div>

            <div className="space-y-3">
              <div className="flex items-center text-sm text-gray-600">
                <Home className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="line-clamp-1">{lease.property?.title || 'Imóvel não especificado'}</span>
              </div>

              <div className="flex items-center text-sm text-gray-600">
                <User className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="line-clamp-1">{lease.tenant?.name || 'Inquilino não especificado'}</span>
              </div>

              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                <span>
                  {lease.startDate && format(new Date(lease.startDate), 'dd/MM/yyyy', { locale: ptBR })}
                  {' - '}
                  {lease.endDate ? format(new Date(lease.endDate), 'dd/MM/yyyy', { locale: ptBR }) : 'Indeterminado'}
                </span>
              </div>

              <div className="pt-3 border-t">
                <p className="text-sm text-gray-600">Valor do aluguel</p>
                <p className="text-2xl font-bold text-primary-600">
                  R$ {lease.rentAmount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}
                </p>
              </div>

              <div className="flex space-x-2 pt-2">
                <button
                  onClick={() => handleOpenModal(lease)}
                  className="flex-1 px-3 py-2 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors flex items-center justify-center space-x-1"
                >
                  <Edit2 className="w-4 h-4" />
                  <span>Editar</span>
                </button>
                <button
                  onClick={() => {
                    setSelectedLease(lease);
                    setShowDeleteModal(true);
                  }}
                  className="flex-1 px-3 py-2 text-sm bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center space-x-1"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Excluir</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredLeases.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nenhum contrato encontrado</p>
        </div>
      )}

      <Modal isOpen={showModal} onClose={handleCloseModal} title={selectedLease ? 'Editar Contrato' : 'Novo Contrato'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Código"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              error={errors.code}
              placeholder="Ex: CON-2025-001"
              required
            />

            <Select
              label="Tipo"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              options={[
                { value: 'RENT', label: 'Locação' },
                { value: 'SALE', label: 'Venda' },
                { value: 'LEASE', label: 'Arrendamento' },
              ]}
              required
            />

            <Select
              label="Status"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              options={[
                { value: 'DRAFT', label: 'Rascunho' },
                { value: 'ACTIVE', label: 'Ativo' },
                { value: 'TERMINATED', label: 'Encerrado' },
                { value: 'SUSPENDED', label: 'Suspenso' },
              ]}
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-4">
            <Select
              label="Imóvel"
              value={formData.propertyId}
              onChange={(e) => setFormData({ ...formData, propertyId: e.target.value })}
              error={errors.propertyId}
              options={[
                { value: '', label: 'Selecione um imóvel' },
                ...properties.map((p) => ({
                  value: p.id,
                  label: `${p.code} - ${p.title}`,
                })),
              ]}
              required
            />

            <Select
              label="Inquilino"
              value={formData.tenantId}
              onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
              error={errors.tenantId}
              options={[
                { value: '', label: 'Selecione um inquilino' },
                ...people
                  .filter((p) => p.name)
                  .map((p) => ({
                    value: p.id,
                    label: `${p.name} ${p.document ? `(${p.document})` : ''}`,
                  })),
              ]}
              required
            />

            <Select
              label="Proprietário"
              value={formData.ownerId}
              onChange={(e) => setFormData({ ...formData, ownerId: e.target.value })}
              options={[
                { value: '', label: 'Selecione um proprietário (opcional)' },
                ...people
                  .filter((p) => p.name)
                  .map((p) => ({
                    value: p.id,
                    label: `${p.name} ${p.document ? `(${p.document})` : ''}`,
                  })),
              ]}
            />

            <Select
              label="Fiador"
              value={formData.guarantorId}
              onChange={(e) => setFormData({ ...formData, guarantorId: e.target.value })}
              options={[
                { value: '', label: 'Selecione um fiador (opcional)' },
                ...people
                  .filter((p) => p.name)
                  .map((p) => ({
                    value: p.id,
                    label: `${p.name} ${p.document ? `(${p.document})` : ''}`,
                  })),
              ]}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Data de Início"
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              error={errors.startDate}
              required
            />

            <Input
              label="Data de Término"
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Valor do Aluguel (R$)"
              type="number"
              value={formData.rentAmount}
              onChange={(e) => setFormData({ ...formData, rentAmount: Number(e.target.value) })}
              error={errors.rentAmount}
              min="0"
              step="0.01"
              required
            />

            <Input
              label="Valor do Depósito/Caução (R$)"
              type="number"
              value={formData.depositAmount}
              onChange={(e) => setFormData({ ...formData, depositAmount: Number(e.target.value) })}
              min="0"
              step="0.01"
            />
          </div>

          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{errors.submit}</div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              {selectedLease ? 'Salvar Alterações' : 'Criar Contrato'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirmar Exclusão" size="sm">
        <div className="space-y-4">
          <p className="text-gray-600">
            Tem certeza que deseja excluir o contrato <strong>{selectedLease?.code}</strong>?
          </p>
          <p className="text-sm text-red-600">Esta ação não pode ser desfeita e removerá todas as cobranças associadas.</p>

          <div className="flex justify-end space-x-3 pt-4">
            <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              Cancelar
            </button>
            <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
              Excluir
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
